import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { LoginPage, ProtectedRoute } from '@/features/auth';

// Lazy load pages for better performance
import { lazy, Suspense } from 'react';
import { PageLoading } from '@/components/common';

// Lazy loaded pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const StudiesListPage = lazy(() => import('@/pages/studies/StudiesListPage'));
const StudyDetailPage = lazy(() => import('@/pages/studies/StudyDetailPage'));
const ParticipantDetailPage = lazy(() => import('@/pages/participants/ParticipantDetailPage'));
const RecordUploadPage = lazy(() => import('@/pages/recording/RecordUploadPage'));
const LiveRecorderPage = lazy(() => import('@/pages/recording/LiveRecorderPage'));
const VideoUploadPage = lazy(() => import('@/pages/recording/VideoUploadPage'));
const LongitudinalAnalysisPage = lazy(() => import('@/pages/analysis/LongitudinalAnalysisPage'));
const InterGroupAnalysisPage = lazy(() => import('@/pages/analysis/InterGroupAnalysisPage'));
const IntraGroupAnalysisPage = lazy(() => import('@/pages/analysis/IntraGroupAnalysisPage'));

// Suspense wrapper for lazy loaded components
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/',
    element: (
      <LazyPage>
        <LandingPage />
      </LazyPage>
    ),
  },
  {
    path: '/login',
    element: <LoginPage />,
  },

  // Protected routes with Layout
  {
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/dashboard',
        element: (
          <LazyPage>
            <DashboardPage />
          </LazyPage>
        ),
      },
      // Studies
      {
        path: '/studies',
        element: (
          <LazyPage>
            <StudiesListPage />
          </LazyPage>
        ),
      },
      {
        path: '/studies/:studyId',
        element: (
          <LazyPage>
            <StudyDetailPage />
          </LazyPage>
        ),
      },
      {
        path: '/studies/:studyId/participants/:participantId',
        element: (
          <LazyPage>
            <ParticipantDetailPage />
          </LazyPage>
        ),
      },
      // Recording
      {
        path: '/record-upload',
        element: (
          <LazyPage>
            <RecordUploadPage />
          </LazyPage>
        ),
      },
      {
        path: '/live-recorder',
        element: (
          <LazyPage>
            <LiveRecorderPage />
          </LazyPage>
        ),
      },
      {
        path: '/video-upload',
        element: (
          <LazyPage>
            <VideoUploadPage />
          </LazyPage>
        ),
      },
      // Analysis
      {
        path: '/data-analysis',
        element: <Navigate to="/data-analysis/longitudinal" replace />,
      },
      {
        path: '/data-analysis/longitudinal',
        element: (
          <LazyPage>
            <LongitudinalAnalysisPage />
          </LazyPage>
        ),
      },
      {
        path: '/data-analysis/inter-group',
        element: (
          <LazyPage>
            <InterGroupAnalysisPage />
          </LazyPage>
        ),
      },
      {
        path: '/data-analysis/intra-group',
        element: (
          <LazyPage>
            <IntraGroupAnalysisPage />
          </LazyPage>
        ),
      },
    ],
  },

  // Catch-all redirect
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
